// Date of birth as three fields — יום | חודש | שנה (2026-10-20). Used by Profile V2 and onboarding.
//
// CONTROLLED: the partial day / month / year strings live with the caller (`parts` / `onPartsChange`), so a
// re-render or remount of the caller never loses what was typed. Validation state (evaluateDobParts) is
// separate from error VISIBILITY: a valid date shows its age at once, but red errors appear only after the
// user leaves the fieldset (blur) or the caller asks for them (`showErrors`, on an attempted submit) —
// never while typing. The API value stays 'YYYY-MM-DD' (evaluateDobParts(...).iso).
import React, { useId, useState } from 'react';
import { evaluateDobParts, parsePastedDob } from '../../utils/age';

const FIELDS = [
  { key: 'day', label: 'יום', max: 2, placeholder: '14', auto: 'bday-day' },
  { key: 'month', label: 'חודש', max: 2, placeholder: '03', auto: 'bday-month' },
  { key: 'year', label: 'שנה', max: 4, placeholder: '1968', auto: 'bday-year' },
];

export function dobStatusText(ev, errorsVisible) {
  if (ev.status === 'valid') return { tone: 'ok', text: `גיל מחושב: ${ev.age}` };
  if (errorsVisible && ev.status === 'invalid_date') return { tone: 'error', text: 'התאריך לא קיים — בדוק יום וחודש' };
  if (errorsVisible && ev.status === 'out_of_range') return { tone: 'error', text: 'בדוק את השנה' };
  if (errorsVisible && ev.status === 'incomplete') return { tone: 'error', text: 'יש להשלים יום, חודש ושנה' };
  return { tone: 'muted', text: 'הגיל יחושב אוטומטית' };
}

export default function DateOfBirthInput({ parts, onPartsChange, showErrors = false, required = false,
                                           todayISO, idPrefix, legend = 'תאריך לידה' }) {
  const autoId = useId();
  const base = idPrefix || `dob-${autoId}`;
  const [touched, setTouched] = useState(false);
  const p = parts || { day: '', month: '', year: '' };
  const ev = evaluateDobParts(p, todayISO);
  const errorsVisible = touched || showErrors;
  const status = dobStatusText(ev, errorsVisible);

  const setField = (key, raw, max) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, max);
    onPartsChange({ ...p, [key]: digits });
  };
  const onPaste = (e) => {
    const full = parsePastedDob(e.clipboardData && e.clipboardData.getData('text'));
    if (full) { e.preventDefault(); onPartsChange(full); }
  };
  // Leaving the whole fieldset (not moving between its own fields) counts as "done typing".
  const onBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setTouched(true);
  };
  const invalid = errorsVisible && ev.status !== 'valid' && ev.status !== 'empty';

  return (
    <fieldset dir="rtl" onBlur={onBlur} data-testid="dob-input" aria-describedby={`${base}-status`}
              aria-invalid={invalid || undefined}>
      <legend className="block text-[13px] text-text-muted mb-1.5">{legend}{required ? ' *' : ''}</legend>
      <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr 1.6fr' }} data-testid="dob-grid">
        {FIELDS.map((f) => (
          <div key={f.key} className="min-w-0">
            <label htmlFor={`${base}-${f.key}`} className="block text-[12px] text-text-muted mb-1 text-center">{f.label}</label>
            <input id={`${base}-${f.key}`} name={f.auto} dir="ltr" inputMode="numeric" maxLength={f.max}
                   autoComplete={f.auto} placeholder={f.placeholder} value={p[f.key] || ''}
                   onChange={(e) => setField(f.key, e.target.value, f.max)} onPaste={onPaste}
                   aria-invalid={invalid || undefined}
                   className={`w-full h-[50px] rounded-control bg-surface-2 border px-2 text-center text-[18px] font-bold text-text tabular-nums placeholder:text-text-muted placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-accent ${invalid ? 'border-quality' : 'border-border'}`} />
          </div>
        ))}
      </div>
      <p id={`${base}-status`} aria-live="polite" data-testid="dob-status" data-tone={status.tone}
         className={`mt-1.5 text-[13px] ${status.tone === 'ok' ? 'text-accent font-semibold' : status.tone === 'error' ? 'text-quality' : 'text-text-muted'}`}>
        {status.text}
      </p>
    </fieldset>
  );
}
